
import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Plus } from 'lucide-react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Progress } from '@/components/ui/progress';
import { CAREER_COUNSELOR_ID } from '@/hooks/useCareerCounselors';

interface CounselorWithDetails {
  id: string;
  name: string;
  phone: string | null;
  min_session_time: number;
  session_charge: number;
  total_bookings: number;
  pending_bookings: number;
}

const CounselorList = () => {
  const [counselors, setCounselors] = useState<CounselorWithDetails[]>([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    fetchCounselors();
  }, []);

  const fetchCounselors = async () => {
    try {
      setLoading(true);
      
      // Get the association ID for Career Counselor
      const { data: associationData, error: associationError } = await supabase
        .from('associations')
        .select('id')
        .eq('name', 'Career Counselor')
        .single();
      
      if (associationError) throw associationError;
      
      // Get all partners with Career Counselor association
      const { data: partnerAssociations, error: partnerError } = await supabase
        .from('partner_associations')
        .select('partner_id')
        .eq('association_id', associationData.id);
      
      if (partnerError) throw partnerError;
      
      if (partnerAssociations.length === 0) {
        setCounselors([]);
        setLoading(false);
        return;
      }
      
      // Get partner IDs
      const partnerIds = partnerAssociations.map(pa => pa.partner_id);
      
      // Get partner details
      const { data: partnersData, error: partnersError } = await supabase
        .from('partners')
        .select('id, name, phone')
        .in('id', partnerIds);
      
      if (partnersError) throw partnersError;
      
      // Get pricing details
      const { data: pricingData, error: pricingError } = await supabase
        .from('partner_pricing')
        .select('partner_id, min_session_time, session_charge')
        .eq('association_id', associationData.id)
        .in('partner_id', partnerIds);
      
      if (pricingError) throw pricingError;
      
      // Get booking statistics
      const { data: bookingsData, error: bookingsError } = await supabase
        .from('bookings')
        .select('partner_id, payment_status')
        .in('partner_id', partnerIds);
      
      if (bookingsError) throw bookingsError;
      
      // Combine all data
      const combinedData = partnersData.map(partner => {
        const pricing = pricingData.find(p => p.partner_id === partner.id) || { min_session_time: 0, session_charge: 0 };
        
        // Filter bookings for this partner
        const partnerBookings = bookingsData.filter(b => b.partner_id === partner.id);
        const totalBookings = partnerBookings.length;
        const pendingBookings = partnerBookings.filter(b => b.payment_status === 'pending').length;
        
        return {
          id: partner.id,
          name: partner.name,
          phone: partner.phone || 'N/A',
          min_session_time: pricing.min_session_time,
          session_charge: pricing.session_charge,
          total_bookings: totalBookings,
          pending_bookings: pendingBookings
        };
      });
      
      setCounselors(combinedData);
    } catch (error) {
      console.error('Error fetching counselors:', error);
      toast.error('Failed to load counselors');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold">Career Counselors</h2>
        <Button 
          onClick={() => navigate('/administrator/career-counselors/add')} 
          className="gap-2"
        >
          <Plus className="h-4 w-4" /> Add New Career Counselor
        </Button>
      </div>
      
      {loading ? (
        <div className="space-y-4">
          <div className="h-8 bg-gray-200 rounded animate-pulse"></div>
          <Progress value={30} className="h-2" />
          <p className="text-center text-sm text-muted-foreground">Loading counselors...</p>
        </div>
      ) : counselors.length === 0 ? (
        <div className="bg-white p-8 text-center rounded-md shadow">
          <p className="text-muted-foreground mb-4">No career counselors found.</p>
          <Button 
            onClick={() => navigate('/administrator/career-counselors/add')}
            className="gap-2"
          >
            <Plus className="h-4 w-4" /> Add Your First Career Counselor
          </Button>
        </div>
      ) : (
        <div className="bg-white rounded-md shadow overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Phone</TableHead>
                <TableHead>Session Time</TableHead>
                <TableHead>Charge (INR)</TableHead>
                <TableHead>Total Bookings</TableHead>
                <TableHead>Pending Bookings</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {counselors.map((counselor) => (
                <TableRow key={counselor.id}>
                  <TableCell className="font-medium">{counselor.name}</TableCell>
                  <TableCell>{counselor.phone}</TableCell>
                  <TableCell>{counselor.min_session_time} min</TableCell>
                  <TableCell>{counselor.session_charge}</TableCell>
                  <TableCell>{counselor.total_bookings}</TableCell>
                  <TableCell>
                    <span className={`${counselor.pending_bookings > 0 ? 'text-yellow-600 font-semibold' : 'text-gray-500'}`}>
                      {counselor.pending_bookings}
                    </span>
                  </TableCell>
                  <TableCell className="text-right">
                    <Button
                      variant="ghost"
                      onClick={() => navigate(`/administrator/career-counselors/edit/${counselor.id}`)}
                    >
                      Edit
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
};

export default CounselorList;
